import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Star, Camera, Lock } from "lucide-react";
import {
  listChecklistTemplates,
  getChecklistTemplate,
  TRADE_KEY_LABELS,
} from "@/services/checklist-template.service";
import type {
  ChecklistTemplateHeader,
  FullChecklistTemplate,
} from "@/services/checklist-template.service";
import {
  JOB_CHECKLIST_AUTHORING_PHASES,
  DEFAULT_JOB_CHECKLIST_PHASE_LABELS,
} from "@/services/job-checklist.service";

// ─── TemplateDetail (read-only) ──────────────────────────────────────────────

function TemplateDetail({ template }: { template: FullChecklistTemplate }) {
  const isGlobal = template.organization_id === null;

  const activePhasesInTemplate = JOB_CHECKLIST_AUTHORING_PHASES.filter((pk) =>
    template.items.some((it) => it.phase_key === pk),
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Global lock banner */}
      {isGlobal && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span>This is a global default template managed by the system.</span>
        </div>
      )}

      {/* Header info */}
      <div className="space-y-1">
        <h4 className="text-sm font-semibold">{template.name}</h4>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {template.trade_key && (
            <span>
              {TRADE_KEY_LABELS[template.trade_key as keyof typeof TRADE_KEY_LABELS] ??
                template.trade_key}
            </span>
          )}
          {template.is_default && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 gap-0.5">
              <Star className="w-2.5 h-2.5" />
              Default
            </Badge>
          )}
          {isGlobal && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
              Global
            </Badge>
          )}
          {!template.is_active && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 text-muted-foreground">
              Inactive
            </Badge>
          )}
        </div>
      </div>

      {/* Phase sections */}
      {activePhasesInTemplate.length === 0 ? (
        <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
          This template has no checklist items.
        </div>
      ) : (
        activePhasesInTemplate.map((pk) => {
          const phaseItems = template.items.filter((it) => it.phase_key === pk);
          const phaseLabel =
            template.phases.find((p) => p.phase_key === pk)?.label ??
            DEFAULT_JOB_CHECKLIST_PHASE_LABELS[pk];

          return (
            <div key={pk} className="rounded-lg border bg-background/40 overflow-hidden">
              <div className="flex items-center justify-between bg-muted/30 border-b px-3 py-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  {phaseLabel}
                </span>
                <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                  {phaseItems.length} {phaseItems.length === 1 ? "item" : "items"}
                </Badge>
              </div>

              <div className="divide-y">
                {phaseItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2.5 px-3 py-2"
                  >
                    <div className="w-4 h-4 rounded border border-border flex-shrink-0" />
                    <span className="text-sm flex-1 text-foreground">{item.label}</span>
                    {item.requires_proof && (
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Camera className="w-3 h-3" />
                        {item.proof_label ? (
                          <span className="max-w-[120px] truncate">{item.proof_label}</span>
                        ) : (
                          <span>Photo</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── ChecklistTemplatesAdmin ─────────────────────────────────────────────────

export function ChecklistTemplatesAdmin() {
  const [templates, setTemplates] = useState<ChecklistTemplateHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FullChecklistTemplate | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const selectRequestRef = useRef(0);

  useEffect(() => {
    setLoading(true);
    listChecklistTemplates()
      .then(setTemplates)
      .finally(() => setLoading(false));
  }, []);

  const selectTemplate = useCallback(async (id: string) => {
    const requestId = ++selectRequestRef.current;
    setSelectedId(id);
    setDetailLoading(true);
    const full = await getChecklistTemplate(id);
    if (requestId !== selectRequestRef.current) return;
    setDetail(full);
    setDetailLoading(false);
  }, []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold">Checklist Templates</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          View installer checklist templates by trade and phase.
        </p>
      </div>

      {/* Split pane */}
      <div className="grid grid-cols-[260px_1fr] gap-4 min-h-[500px]">
        {/* Left: template list */}
        <div className="rounded-lg border bg-background/40 flex flex-col">
          <div className="px-3 py-2 border-b">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Templates
            </span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="space-y-2 p-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-8 bg-muted animate-pulse rounded" />
                ))}
              </div>
            ) : templates.length === 0 ? (
              <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
                No templates found.
              </div>
            ) : (
              <div className="divide-y">
                {templates.map((t) => {
                  const tradeLabel =
                    t.trade_key && TRADE_KEY_LABELS[t.trade_key as keyof typeof TRADE_KEY_LABELS]
                      ? TRADE_KEY_LABELS[t.trade_key as keyof typeof TRADE_KEY_LABELS]
                      : t.trade_key ?? "—";
                  const isSelected = t.id === selectedId;
                  return (
                    <button
                      key={t.id}
                      className={`w-full text-left px-3 py-2 hover:bg-muted/40 transition-colors ${
                        isSelected ? "bg-muted/60" : ""
                      }`}
                      onClick={() => selectTemplate(t.id)}
                    >
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-xs font-medium truncate flex-1 min-w-0">
                          {t.name}
                        </span>
                        {t.is_default && (
                          <Star className="w-3 h-3 text-amber-500 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-muted-foreground">{tradeLabel}</span>
                        {t.organization_id === null && (
                          <Badge variant="secondary" className="text-[9px] h-3.5 px-1">
                            Global
                          </Badge>
                        )}
                        {!t.is_active && (
                          <Badge
                            variant="outline"
                            className="text-[9px] h-3.5 px-1 text-muted-foreground"
                          >
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: detail view */}
        <div className="rounded-lg border bg-background/40 overflow-y-auto">
          {detailLoading ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-8 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : detail ? (
            <TemplateDetail template={detail} />
          ) : (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
              Select a template to view its items.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
